/**
 * Breakpoints component - Mutual exclusive selection of breakpoints using IconButton
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import "./Breakpoints.scss";

export type BreakpointType = "desktop_large" | "desktop" | "tablet" | "mobile";

export interface BreakpointsConfig {
  readonly breakpoints: readonly BreakpointType[];
  readonly selected?: BreakpointType;
  readonly onSelectionChange?: (breakpoint: BreakpointType) => void;
}

export interface BreakpointsAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelected: (breakpoint: BreakpointType) => void;
  readonly getSelected: () => BreakpointType | null;
  readonly destroy: () => void;
}

/**
 * Creates a Breakpoints component with mutual exclusive selection
 */
export const createBreakpoints = (
  config: BreakpointsConfig
): BreakpointsAPI => {
  const { breakpoints, selected, onSelectionChange } = config;

  let currentSelected: BreakpointType | null;
  if (selected !== undefined) {
    currentSelected = selected;
  } else if (breakpoints.length > 0) {
    currentSelected = breakpoints[0]!; // Safe because we check length > 0
  } else {
    currentSelected = null;
  }
  let isDestroyed = false;

  // Map to store IconButton instances
  const buttonMap = new Map<BreakpointType, IconButtonAPI>();

  // Create container
  const container = document.createElement("div");
  container.className = "breakpoints";

  /**
   * Update all buttons to reflect current selection
   */
  const updateSelection = (): void => {
    if (isDestroyed) return;

    breakpoints.forEach((breakpoint) => {
      const button = buttonMap.get(breakpoint);
      if (button) {
        button.setSelected(breakpoint === currentSelected);
      }
    });
  };

  /**
   * Handle breakpoint selection
   */
  const handleBreakpointClick = (clickedBreakpoint: BreakpointType): void => {
    if (isDestroyed || clickedBreakpoint === currentSelected) return;

    // Update selection
    currentSelected = clickedBreakpoint;
    updateSelection();

    // Fire callback
    onSelectionChange?.(clickedBreakpoint);

    console.log(`Breakpoint selected: ${clickedBreakpoint}`);
  };

  /**
   * Initialize all breakpoint buttons
   */
  const initializeButtons = (): void => {
    breakpoints.forEach((breakpoint) => {
      try {
        const button = createIconButton({
          icon: `breakpoints_${breakpoint}`,
          size: 30,
          iconSize: 28,
          behavior: "switch",
          selected: breakpoint === currentSelected,
          className: `breakpoints__button breakpoints__button--${breakpoint}`,
          onClick: () => handleBreakpointClick(breakpoint),
        });

        buttonMap.set(breakpoint, button);
        container.appendChild(button.getElement());
      } catch (error) {
        console.error(
          `Failed to create breakpoint button for ${breakpoint}:`,
          error
        );

        // Create fallback button
        const fallbackButton = document.createElement("div");
        fallbackButton.className = `breakpoints__button breakpoints__button--${breakpoint} breakpoints__button--fallback`;
        fallbackButton.textContent = breakpoint.charAt(0).toUpperCase();
        fallbackButton.addEventListener("click", () =>
          handleBreakpointClick(breakpoint)
        );
        container.appendChild(fallbackButton);
      }
    });
  };

  // Initialize component
  initializeButtons();

  // Public API
  const api: BreakpointsAPI = {
    getElement: () => container,

    setSelected: (breakpoint: BreakpointType) => {
      if (isDestroyed) return;

      if (breakpoints.includes(breakpoint)) {
        currentSelected = breakpoint;
        updateSelection();
      } else {
        console.warn(
          `Breakpoint "${breakpoint}" is not available in current configuration`
        );
      }
    },

    getSelected: () => currentSelected,

    destroy: () => {
      if (isDestroyed) return;
      isDestroyed = true;

      // Destroy all IconButton instances
      buttonMap.forEach((button) => {
        button.destroy();
      });
      buttonMap.clear();

      container.remove();
    },
  };

  return api;
};

/**
 * Utility function to create and append Breakpoints to container
 */
export const createBreakpointsInContainer = (
  container: HTMLElement,
  config: BreakpointsConfig
): BreakpointsAPI => {
  const breakpoints = createBreakpoints(config);
  container.appendChild(breakpoints.getElement());
  return breakpoints;
};
